import { faDownload, faRefresh, faSpinner } from '@fortawesome/pro-regular-svg-icons';
import {
  faChevronRight,
  faCircleCheck,
  faCircleXmark,
  faClock,
  faSquareCheck,
  faTrashCan,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cva } from 'class-variance-authority';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Button } from '@boss/ui';

import OrderImportProcessingSkeleton from './OrderImportProcessingSkeleton';
import type { OrderImportProcessResponse, ProcessedOrderImportLine } from '../../../client-queries';
import { exportOrderImportResult, getSourceColumns } from '../utils/orderImportExport';

type OrderImportProcessingViewProps = {
  title: string;
  fileName: string;
  isLoading: boolean;
  isRevalidating: boolean;
  isUploadingProducts: boolean;
  result: OrderImportProcessResponse | null;
  onRevalidate: () => void | Promise<void>;
  onUploadProducts: () => void | Promise<void>;
  onStartOver: () => void;
  onDeleteRow: (rowIndex: number) => void;
  onRowFieldChange: (payload: {
    rowIndex: number;
    field: 'skuId' | 'myMixColorId' | 'quantity';
    value: string;
  }) => void;
  isUploadProductsDisabled: boolean;
  toEditableFields: (row: ProcessedOrderImportLine) => {
    skuId: boolean;
    myMixColorId: boolean;
    quantity: boolean;
  };
  mode?: 'page' | 'modal';
};

const TEXT_COLOR = 'text-green-dark';
const SUCCESS_TEXT_COLOR = 'text-green';
const SPINNING_CLASS = '[&_svg]:animate-spin'; // Tailwind class to spin the

const ContainerStyles = cva('');
const HeaderStyles = cva('mb-2');
const HeaderRowStyles = cva('mb-6 flex items-center gap-4');
const HeaderIconBadgeStyles = cva('flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-accentBg');
const HeaderIconStyles = cva(`${TEXT_COLOR} text-xl`);
const HeaderTitleStyles = cva(`${TEXT_COLOR}`);
const HeaderFileNameStyles = cva('mt-1 text-lg text-orderImport-step-description-text');

const TableShellStyles = cva(
  'overflow-hidden rounded-orderImport-upload-section border border-orderImport-upload-section-border',
);
const TableHeaderStyles = cva(
  'hidden grid-cols-[1.2fr_0.8fr_1.2fr_1fr_1.4fr_64px] border-b-1 border-orderImport-upload-section-border px-4 py-3 text-sm font-semibold text-orderImport-step-description-text md:grid',
);
const TableRowsStyles = cva('');
const TableRowStyles = cva(
  'grid grid-cols-1 gap-2 border-b-1 border-orderImport-upload-section-border px-4 py-3 last:border-b-0 md:grid-cols-[1.2fr_0.8fr_1.2fr_1fr_1.4fr_64px] md:items-center',
);
const FieldLabelStyles = cva('text-sm text-orderImport-step-description-text md:hidden');
const StatusWrapperStyles = cva('inline-flex items-center gap-2 rounded px-2 py-1 text-sm font-semibold');
const StatusIconStyles = cva('shrink-0', {
  variants: {
    tone: {
      success: SUCCESS_TEXT_COLOR,
      error: 'text-red',
    },
  },
});
const StatusTextStyles = cva('font-semibold', {
  variants: {
    tone: {
      success: SUCCESS_TEXT_COLOR,
      error: 'text-red',
    },
  },
});
const FooterStyles = cva('mt-6 flex flex-wrap justify-between gap-3');
const ErrorInputStyles = cva(
  'w-full max-w-full min-w-0 rounded border-1 border-red/35 bg-red/10 px-2 py-1 text-sm font-semibold text-red',
);
const CellTextStyles = cva('text-sm text-green-dark');
const RowActionButtonStyles = cva(
  'text-orderImport-step-description-text hover:text-green-dark disabled:cursor-not-allowed disabled:opacity-40',
);

const toStatusTone = (row: ProcessedOrderImportLine) => {
  return row.errors.length > 0 ? 'error' : 'success';
};

const getArticleTitle = (row: ProcessedOrderImportLine, fallbackLabel: string) => {
  return row.enrichment?.myMix.data?.name || row.enrichment?.product.data?.name || row.description || fallbackLabel;
};

type OrderImportRowProps = {
  row: ProcessedOrderImportLine;
  rowIndex: number;
  onDeleteRow: (rowIndex: number) => void;
  onRowFieldChange: (payload: {
    rowIndex: number;
    field: 'skuId' | 'myMixColorId' | 'quantity';
    value: string;
  }) => void;
  toEditableFields: (row: ProcessedOrderImportLine) => {
    skuId: boolean;
    myMixColorId: boolean;
    quantity: boolean;
  };
  labels: {
    skuId: string;
    colorCode: string;
    quantity: string;
    description: string;
    unknownSku: string;
    deleteRow: string;
    statusSuccess: string;
    statusError: string;
  };
  statusMessages: string[];
  'data-testid'?: string;
};

const OrderImportRow = ({
  row,
  rowIndex,
  onDeleteRow,
  onRowFieldChange,
  toEditableFields,
  labels,
  statusMessages,
  'data-testid': dataTestId,
}: OrderImportRowProps) => {
  const tone = toStatusTone(row);
  const editableFields = toEditableFields(row);
  const isErrorRow = row.errors.length > 0;

  const renderCell = ({
    isEditable,
    value,
    onChange,
    inputType = 'text',
    inputClassName,
    type,
  }: {
    isEditable: boolean;
    value: string | number | undefined;
    onChange: (value: string) => void;
    inputType?: 'text' | 'number';
    inputClassName?: string;
    type: string;
  }) => {
    if (isErrorRow && isEditable) {
      return (
        <input
          className={twMerge(ErrorInputStyles(), inputClassName)}
          name={`row-${type}-${rowIndex}`}
          onChange={event => onChange(event.target.value)}
          type={inputType}
          value={value ?? ''}
        />
      );
    }

    return <p className={CellTextStyles()}>{String(value ?? '')}</p>;
  };

  return (
    <div className={twMerge(TableRowStyles(), isErrorRow && 'bg-red/5')} data-testid={dataTestId}>
      <div className="min-w-0">
        <p className={FieldLabelStyles()}>{labels.skuId}</p>
        {renderCell({
          isEditable: editableFields.skuId,
          value: row.skuId || '',
          type: 'skuId',
          onChange: value => {
            onRowFieldChange({
              rowIndex,
              field: 'skuId',
              value,
            });
          },
        })}
      </div>

      <div className="min-w-0">
        <p className={FieldLabelStyles()}>{labels.quantity}</p>
        {renderCell({
          isEditable: editableFields.quantity,
          value: row.quantity ?? '',
          type: 'quantity',
          onChange: value => {
            onRowFieldChange({
              rowIndex,
              field: 'quantity',
              value,
            });
          },
          inputType: 'number',
          inputClassName: 'text-center',
        })}
      </div>

      <div>
        <p className={FieldLabelStyles()}>{labels.description}</p>
        <p className="text-green-dark text-sm">{getArticleTitle(row, labels.unknownSku)}</p>
      </div>

      <div className="min-w-0">
        <p className={FieldLabelStyles()}>{labels.colorCode}</p>
        {renderCell({
          isEditable: editableFields.myMixColorId,
          value: editableFields.myMixColorId
            ? row.myMixColorId || ''
            : row.enrichment?.color?.data?.colorCode || row.myMixColorId || '',
          type: 'myMixColorId',
          onChange: value => {
            onRowFieldChange({
              rowIndex,
              field: 'myMixColorId',
              value,
            });
          },
        })}
      </div>

      {tone === 'success' ? (
        <div className={StatusWrapperStyles()}>
          <FontAwesomeIcon
            aria-label={labels.statusSuccess}
            className={StatusIconStyles({ tone })}
            icon={faCircleCheck}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          {statusMessages.map((message, index) => {
            return (
              <div className={StatusWrapperStyles()} key={`${row.rowId || row.lineNumber}-status-${index}`}>
                <FontAwesomeIcon
                  aria-label={labels.statusError}
                  className={StatusIconStyles({ tone })}
                  icon={faCircleXmark}
                />
                <span className={StatusTextStyles({ tone })}>{message}</span>
              </div>
            );
          })}
        </div>
      )}

      <button
        aria-label={labels.deleteRow}
        className={twMerge(RowActionButtonStyles(), 'flex w-full justify-end md:w-auto')}
        data-testid="order-import-delete-row-button"
        onClick={() => onDeleteRow(rowIndex)}
        type="button"
      >
        <FontAwesomeIcon icon={faTrashCan} />
      </button>
    </div>
  );
};

type OrderImportFooterProps = {
  isExportingResult: boolean;
  isRevalidating: boolean;
  isUploadingProducts: boolean;
  hasRows: boolean;
  isUploadProductsDisabled: boolean;
  labels: {
    exportButton: string;
    exportingButton: string;
    revalidateButton: string;
    revalidatingButton: string;
    uploadProductsButton: string;
  };
  onExport: () => void;
  onRevalidate: () => void | Promise<void>;
  onUploadProducts: () => void | Promise<void>;
};

const OrderImportFooter = ({
  isExportingResult,
  isRevalidating,
  isUploadingProducts,
  hasRows,
  isUploadProductsDisabled,
  labels,
  onExport,
  onRevalidate,
  onUploadProducts,
}: OrderImportFooterProps) => {
  return (
    <div className={FooterStyles()}>
      <div className="flex flex-wrap gap-3">
        <Button
          className={twMerge(isExportingResult && SPINNING_CLASS)}
          data-testid="order-import-export-button"
          disabled={isExportingResult}
          icon={isExportingResult ? faSpinner : faDownload}
          iconPosition="left"
          label={isExportingResult ? labels.exportingButton : labels.exportButton}
          onClick={onExport}
          type="secondary"
        />
        <Button
          className={twMerge(isRevalidating && SPINNING_CLASS)}
          data-testid="order-import-revalidate-button"
          disabled={isRevalidating || !hasRows}
          icon={isRevalidating ? faSpinner : faRefresh}
          iconPosition="left"
          label={isRevalidating ? labels.revalidatingButton : labels.revalidateButton}
          onClick={onRevalidate}
          type="secondary"
        />
      </div>
      <Button
        className={twMerge(isUploadingProducts && SPINNING_CLASS)}
        data-testid="order-import-upload-products-button"
        disabled={isUploadingProducts || isUploadProductsDisabled}
        icon={isUploadingProducts ? faSpinner : faChevronRight}
        iconPosition="right"
        label={labels.uploadProductsButton}
        onClick={onUploadProducts}
        type="primary"
      />
    </div>
  );
};

const OrderImportProcessingView = ({
  title,
  fileName,
  isLoading,
  isRevalidating,
  isUploadingProducts,
  result,
  onRevalidate,
  onUploadProducts,
  onStartOver,
  onDeleteRow,
  onRowFieldChange,
  isUploadProductsDisabled,
  toEditableFields,
  mode = 'page',
}: OrderImportProcessingViewProps) => {
  const { t } = useTranslation('account');
  const [isExportingResult, setIsExportingResult] = useState(false);

  const resolveErrorMessage = (errorKey: string) => {
    const translated = String(t(errorKey));

    return translated === errorKey ? errorKey : translated;
  };

  const getStatusMessages = (row: ProcessedOrderImportLine) => {
    if (!row.errors.length) {
      return [String(t('orderImport.upload.status.loaded'))];
    }

    return row.errors.map(resolveErrorMessage);
  };

  const sourceColumns = result?.sourceColumns ?? {
    skuId: String(t('orderImport.upload.columns.skuId')),
    quantity: String(t('orderImport.upload.columns.quantity')),
    description: String(t('orderImport.upload.columns.description')),
    myMixColorId: String(t('orderImport.upload.columns.colorCode')),
  };

  const labels = {
    skuId: sourceColumns.skuId,
    colorCode: sourceColumns.myMixColorId,
    quantity: sourceColumns.quantity,
    description: sourceColumns.description,
    status: String(t('orderImport.upload.columns.status')),
    actions: String(t('orderImport.upload.columns.actions')),
    validRows: String(t('orderImport.upload.validRowsLabel')),
    invalidRows: String(t('orderImport.upload.invalidRowsLabel')),
    unknownSku: String(t('orderImport.upload.unknownSku')),
    deleteRow: String(t('orderImport.upload.deleteRow')),
    statusSuccess: String(t('orderImport.upload.status.success')),
    statusError: String(t('orderImport.upload.status.error')),
    exportButton: String(t('orderImport.upload.export.downloadButton')),
    exportingButton: String(t('orderImport.upload.export.downloadingButton')),
    revalidateButton: String(t('orderImport.upload.revalidateButton')),
    revalidatingButton: String(t('orderImport.upload.revalidatingButton')),
    uploadProductsButton: String(t('orderImport.upload.uploadProductsButton')),
  };

  const handleExportResult = async () => {
    if (!result || isExportingResult) {
      return;
    }

    setIsExportingResult(true);

    try {
      const sourceColumns = getSourceColumns(result, t);
      const errorColumnLabel = String(t('orderImport.upload.export.errorColumn'));
      const unprocessedSheetName = String(t('orderImport.upload.export.unprocessedSheetName'));
      const allRowsSheetName = String(t('orderImport.upload.export.allRowsSheetName'));

      await exportOrderImportResult(
        result,
        fileName,
        sourceColumns,
        errorColumnLabel,
        unprocessedSheetName,
        allRowsSheetName,
        resolveErrorMessage,
      );
    } catch (_error) {
      // Error is already captured in the utility
    } finally {
      setIsExportingResult(false);
    }
  };

  return (
    <div className={ContainerStyles()} data-testid="order-import-processing-view">
      <div className={HeaderStyles()}>
        <div className={HeaderRowStyles()}>
          <div className={HeaderIconBadgeStyles()}>
            <FontAwesomeIcon
              aria-label={
                isLoading ? String(t('orderImport.upload.processing')) : String(t('orderImport.upload.complete'))
              }
              className={HeaderIconStyles()}
              icon={isLoading ? faClock : faSquareCheck}
            />
          </div>
          <div className="flex flex-1 flex-wrap items-start justify-between gap-3">
            <div>
              <h4 className={HeaderTitleStyles()}>{title}</h4>
              <p className={HeaderFileNameStyles()} data-testid="order-import-processing-filename">
                {fileName}
              </p>
            </div>
            {mode === 'page' && (
              <Button
                data-testid="order-import-start-over-button"
                label={String(t('orderImport.upload.startOver'))}
                onClick={onStartOver}
                type="link"
              />
            )}
          </div>
        </div>
      </div>

      {isLoading || !result ? (
        <OrderImportProcessingSkeleton data-testid="order-import-processing-skeleton" />
      ) : (
        <>
          <div className={TableShellStyles()} data-testid="order-import-table">
            <div className={TableHeaderStyles()} data-testid="order-import-table-header">
              <span>{labels.skuId}</span>
              <span>{labels.quantity}</span>
              <span>{labels.description}</span>
              <span>{labels.colorCode}</span>
              <span>{labels.status}</span>
              <span />
            </div>

            <div className={TableRowsStyles()} data-testid="order-import-table-rows">
              {result.rows.map((row: ProcessedOrderImportLine, rowIndex: number) => {
                return (
                  <OrderImportRow
                    data-testid={`order-import-row-${rowIndex}`}
                    key={`${row.rowId || row.lineNumber}-${rowIndex}`}
                    labels={labels}
                    onDeleteRow={onDeleteRow}
                    onRowFieldChange={onRowFieldChange}
                    row={row}
                    rowIndex={rowIndex}
                    statusMessages={getStatusMessages(row)}
                    toEditableFields={toEditableFields}
                  />
                );
              })}
            </div>
          </div>

          <OrderImportFooter
            hasRows={result.rows.length > 0}
            isExportingResult={isExportingResult}
            isRevalidating={isRevalidating}
            isUploadProductsDisabled={isUploadProductsDisabled}
            isUploadingProducts={isUploadingProducts}
            labels={labels}
            onExport={handleExportResult}
            onRevalidate={onRevalidate}
            onUploadProducts={onUploadProducts}
          />
        </>
      )}
    </div>
  );
};

export default OrderImportProcessingView;
